// src/utils/charts/formatters.ts
/**
 * Chart Formatters
 * 
 * Standardized formatting functions for chart values, tooltips, and axis labels.
 * Consolidates repeated formatting patterns across the codebase.
 */

// ============================================================================
// CURRENCY FORMATTERS
// ============================================================================

export interface CurrencyOptions {
  decimals?: number;
  compact?: boolean;
  showSign?: boolean;
}

/**
 * Format number as USD currency
 * Supports compact notation (K, M, B, T) and configurable decimals
 */
export function formatCurrency(value: number, options: CurrencyOptions = {}): string {
  const { decimals = 2, compact = true, showSign = false } = options;
  const sign = showSign && value > 0 ? '+' : '';
  const abs = Math.abs(value);
  
  if (!Number.isFinite(value)) return '—';
  
  if (compact) {
    if (abs >= 1e12) return `${sign}$${(value / 1e12).toFixed(decimals)}T`;
    if (abs >= 1e9) return `${sign}$${(value / 1e9).toFixed(decimals)}B`;
    if (abs >= 1e6) return `${sign}$${(value / 1e6).toFixed(decimals)}M`;
    if (abs >= 1e3) return `${sign}$${(value / 1e3).toFixed(decimals)}K`;
  }
  
  if (abs >= 1) {
    return `${sign}$${value.toLocaleString('en-US', { 
      minimumFractionDigits: Math.min(decimals, 2), 
      maximumFractionDigits: decimals 
    })}`;
  }
  
  // Small values (< $1)
  return `${sign}$${value.toFixed(Math.max(decimals, 4))}`;
}

/**
 * Short currency format for axis labels
 */
export function formatCurrencyShort(value: number): string {
  return formatCurrency(value, { decimals: 1, compact: true });
}

/**
 * Full currency format for tooltips
 */
export function formatCurrencyFull(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ============================================================================
// PERCENT FORMATTERS
// ============================================================================

export interface PercentOptions {
  decimals?: number;
  showSign?: boolean;
  multiply?: boolean; // true if value needs *100
}

/**
 * Format number as percentage
 */
export function formatPercent(value: number, options: PercentOptions = {}): string {
  const { decimals = 2, showSign = false, multiply = false } = options;
  
  if (!Number.isFinite(value)) return '—';
  
  const pct = multiply ? value * 100 : value;
  const sign = showSign && pct > 0 ? '+' : '';
  
  // Adaptive decimals for small values
  const abs = Math.abs(pct);
  let actualDecimals = decimals;
  if (abs < 0.01) actualDecimals = Math.max(decimals, 4);
  else if (abs < 0.1) actualDecimals = Math.max(decimals, 3);
  
  return `${sign}${pct.toFixed(actualDecimals)}%`;
}

/**
 * Format as percentage with sign
 */
export function formatPercentSigned(value: number, decimals: number = 2): string {
  return formatPercent(value, { decimals, showSign: true });
}

/**
 * Format decimal as percentage (multiply by 100)
 */
export function formatDecimalAsPercent(value: number, decimals: number = 2): string {
  return formatPercent(value, { decimals, multiply: true });
}

// ============================================================================
// NUMBER FORMATTERS
// ============================================================================

export interface NumberOptions {
  decimals?: number;
  compact?: boolean;
  unit?: string;
}

/**
 * Format number with optional unit and compact notation
 */
export function formatNumber(value: number, options: NumberOptions = {}): string {
  const { decimals = 2, compact = false, unit } = options;
  
  if (!Number.isFinite(value)) return '—';
  
  let formatted: string;
  const abs = Math.abs(value);
  
  if (compact) {
    if (abs >= 1e12) formatted = `${(value / 1e12).toFixed(decimals)}T`;
    else if (abs >= 1e9) formatted = `${(value / 1e9).toFixed(decimals)}B`;
    else if (abs >= 1e6) formatted = `${(value / 1e6).toFixed(decimals)}M`;
    else if (abs >= 1e3) formatted = `${(value / 1e3).toFixed(decimals)}K`;
    else formatted = value.toFixed(decimals);
  } else if (abs >= 1) {
    formatted = value.toLocaleString('en-US', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: decimals 
    });
  } else if (abs === 0) {
    formatted = '0';
  } else {
    // Small numbers - adaptive precision
    const sigFigs = abs < 1e-6 ? 10 : abs < 1e-4 ? 8 : abs < 0.01 ? 6 : 4;
    formatted = value.toFixed(Math.min(sigFigs, 10));
  }
  
  return unit ? `${formatted} ${unit}` : formatted;
}

/**
 * Format ratio value (e.g., Sharpe, Sortino)
 */
export function formatRatio(value: number, decimals: number = 2): string {
  if (!Number.isFinite(value)) return '—';
  return value.toFixed(decimals);
}

/**
 * Format multiplier (e.g., 2.5x)
 */
export function formatMultiplier(value: number, decimals: number = 2): string {
  if (!Number.isFinite(value)) return '—';
  return `${value.toFixed(decimals)}x`;
}

// ============================================================================
// AXIS FORMATTERS
// ============================================================================

export type AxisFormatterFn = (value: number) => string;

/**
 * Create axis formatter for currency values
 */
export function currencyAxisFormatter(): AxisFormatterFn {
  return (value: number) => formatCurrencyShort(value);
}

/**
 * Create axis formatter for percentage values
 */
export function percentAxisFormatter(decimals: number = 1): AxisFormatterFn {
  return (value: number) => `${value.toFixed(decimals)}%`;
}

/**
 * Create axis formatter for ratio values
 */
export function ratioAxisFormatter(decimals: number = 1): AxisFormatterFn {
  return (value: number) => value.toFixed(decimals);
}

/**
 * Create axis formatter for compact numbers
 */
export function compactAxisFormatter(decimals: number = 1): AxisFormatterFn {
  return (value: number) => formatNumber(value, { decimals, compact: true });
}

/**
 * Get appropriate axis formatter based on metric type
 */
export function getAxisFormatter(metricType: 'currency' | 'percent' | 'ratio' | 'number'): AxisFormatterFn {
  switch (metricType) {
    case 'currency':
      return currencyAxisFormatter();
    case 'percent':
      return percentAxisFormatter();
    case 'ratio':
      return ratioAxisFormatter();
    default:
      return compactAxisFormatter();
  }
}

// ============================================================================
// TOOLTIP FORMATTERS
// ============================================================================

export interface TooltipValue {
  name: string;
  value: number;
  color?: string;
  format?: 'currency' | 'percent' | 'ratio' | 'number';
}

/**
 * Format a single tooltip line
 */
export function formatTooltipLine(item: TooltipValue): string {
  const { name, value, color, format = 'number' } = item;
  
  let formattedValue: string;
  switch (format) {
    case 'currency':
      formattedValue = formatCurrency(value);
      break;
    case 'percent':
      formattedValue = formatPercent(value, { showSign: true });
      break;
    case 'ratio':
      formattedValue = formatRatio(value);
      break;
    default:
      formattedValue = formatNumber(value);
  }
  
  const colorStyle = color ? `color:${value >= 0 ? '#22c55e' : '#ef4444'}` : 'color:#f3f4f6';
  return `${name}: <span style="${colorStyle}">${formattedValue}</span>`;
}

/**
 * Format tooltip header (date)
 */
export function formatTooltipHeader(date: string | number | Date): string {
  const d = date instanceof Date ? date : new Date(typeof date === 'number' ? date : Date.parse(date));
  const formatted = d.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  });
  return `<div style="font-size:11px;font-weight:500;margin-bottom:4px;color:#9ca3af;">${formatted}</div>`;
}

/**
 * Build complete tooltip HTML
 */
export function buildTooltipHtml(header: string, lines: string[]): string {
  return `${header}${lines.join('<br/>')}`;
}

// ============================================================================
// COLOR VALUE FORMATTERS
// ============================================================================

/**
 * Get color based on value sign
 */
export function getSignColor(value: number): string {
  if (!Number.isFinite(value)) return '#94a3b8';
  return value >= 0 ? '#22c55e' : '#ef4444';
}

/**
 * Get color based on value relative to threshold
 */
export function getThresholdColor(
  value: number, 
  threshold: number = 0,
  positiveColor: string = '#22c55e',
  negativeColor: string = '#ef4444'
): string {
  return value >= threshold ? positiveColor : negativeColor;
}

// ============================================================================
// DATE FORMATTERS
// ============================================================================

/**
 * Format timestamp to display label
 */
export function formatDateLabel(
  timestamp: number | string | Date,
  format: 'short' | 'medium' | 'long' = 'medium'
): string {
  const ts = timestamp instanceof Date 
    ? timestamp.getTime()
    : typeof timestamp === 'number' 
    ? timestamp 
    : Date.parse(timestamp);
  
  if (!Number.isFinite(ts)) return String(timestamp);
  
  const date = new Date(ts);
  
  switch (format) {
    case 'short':
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case 'medium':
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    case 'long':
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    default:
      return date.toISOString().slice(0, 10);
  }
}

// ============================================================================
// LEGACY EXPORTS (for backward compatibility)
// ============================================================================

export function formatUsd(value: number): string {
  return formatCurrencyFull(value);
}

export function formatUsdBillions(value: number): string {
  return formatCurrency(value / 1e9, { decimals: 2 }) + 'B';
}

export function formatPrice(value: number): string {
  return formatCurrencyFull(value);
}

export function formatPct(decimals = 2) {
  return (v: number | string) => {
    const num = typeof v === 'string' ? parseFloat(v) : v;
    return `${num.toFixed(decimals)}%`;
  };
}

export function formatFundingRate(value: number, decimals = 3): string {
  return `${Number(value).toFixed(decimals)}%`;
}
